import rows from '../prompt-map-v6d.csv';
import { slugifyAltShortLabel } from './lib/slug';
import type {
  AnswerPrompt,
  Area,
  AreaTarget,
  ChoiceQuestion,
  ColorIndex,
  Option,
  PhotoQuestion,
  Question,
  Target,
} from './types';

const PROMPT_LABELS: Record<string, string> = {
  Q1: 'SCENE',
  Q2: 'CO-CONSPIRATOR',
  Q3: 'HOBBIES',
};

const HEADINGS: Record<string, string> = {
  Q1: 'The Setting (The Revolution)',
  Q2: 'The Characters',
  Q3: 'The Details',
  PHOTO: 'You in the Story',
};

const HINTS: Record<string, string> = {
  Q1: 'On the Blanco label, Ramón and Guadalupe are shown tearing down oppression and imposed rules.',
  Q2: 'The Espolon label is full of characters joining the scene: friends, animals, everyday rebels.',
  Q3: "Look closely at the label and you'll spot small details that bring the scene to life.",
  PHOTO: 'Ready to ride the rooster? Add your photo to become part of the label.',
};

const OR_PREFIX = /^OR\s+/i;

let promptSeq = 0;
function nextPromptId(): string {
  return `ap-${Date.now().toString(36)}-${(promptSeq++).toString(36)}`;
}

interface ParsedCell {
  isOr: boolean;
  text: string;
}

function parseOr(raw: string): ParsedCell {
  const trimmed = raw.trim();
  if (OR_PREFIX.test(trimmed)) {
    return { isOr: true, text: trimmed.replace(OR_PREFIX, '').trim() };
  }
  return { isOr: false, text: trimmed };
}

function buildPromptsForAnswer(
  answerRows: Array<(typeof rows)[number]>,
): AnswerPrompt[] {
  const out: AnswerPrompt[] = [];
  let currentGroup: AnswerPrompt | null = null;
  let currentTarget = '';

  for (const row of answerRows) {
    if (!row.injects) {
      currentGroup = null;
      currentTarget = '';
      continue;
    }
    const ut = parseOr(row.updateTargets);
    const pi = parseOr(row.promptText);
    const isContinuation = ut.isOr || (ut.text === '' && pi.isOr);
    const target = ut.text !== '' ? ut.text : currentTarget;

    if (isContinuation && currentGroup) {
      if (!target || !pi.text) continue;
      currentGroup.alternatives.push({
        target,
        text: pi.text,
        shortLabel: slugifyAltShortLabel(pi.text),
      });
      if (ut.text !== '') currentTarget = ut.text;
      continue;
    }

    if (!target || !pi.text) {
      currentGroup = null;
      currentTarget = target || currentTarget;
      continue;
    }

    currentGroup = {
      id: nextPromptId(),
      alternatives: [
        { target, text: pi.text, shortLabel: slugifyAltShortLabel(pi.text) },
      ],
    };
    out.push(currentGroup);
    currentTarget = target;
  }

  return out;
}

export function buildQuestions(): Question[] {
  const byQ = new Map<string, typeof rows>();
  for (const row of rows) {
    if (!row.qId) continue;
    const bucket = byQ.get(row.qId) ?? [];
    bucket.push(row);
    byQ.set(row.qId, bucket);
  }

  const result: Question[] = [];
  let choiceIndex = 0;

  const entries = Array.from(byQ.entries());
  const photoIdx = entries.findIndex(([qId]) => qId === 'PHOTO');
  if (photoIdx > 0) {
    const [photoEntry] = entries.splice(photoIdx, 1);
    entries.unshift(photoEntry);
  }

  for (const [qId, group] of entries) {
    const first = group[0];

    if (qId === 'PHOTO') {
      const answerGroups = groupByAnswer(group);
      const prompts: AnswerPrompt[] = [];
      for (const ag of answerGroups) prompts.push(...buildPromptsForAnswer(ag));
      const photo: PhotoQuestion = {
        kind: 'photo',
        id: qId,
        heading: HEADINGS[qId] ?? '',
        hint: HINTS[qId] ?? '',
        title: first.questionText,
        description: first.imageryImpact,
        file: null,
        prompts,
        randomMode: 'shuffle',
        cycleIndex: 0,
      };
      result.push(photo);
      continue;
    }

    const colorIndex = (choiceIndex % 4) as ColorIndex;
    choiceIndex++;

    const answerGroups = groupByAnswer(group);
    const options: Option[] = [];
    for (const ag of answerGroups) {
      const head = ag[0];
      if (!head.answerLabel) continue;
      const injectsAnywhere = ag.some((r) => r.injects);
      const prompts = injectsAnywhere ? buildPromptsForAnswer(ag) : [];
      options.push({
        label: head.answerLabel,
        injects: injectsAnywhere,
        prompts,
      });
    }

    const choice: ChoiceQuestion = {
      kind: 'choice',
      id: qId,
      heading: HEADINGS[qId] ?? '',
      hint: HINTS[qId] ?? '',
      title: first.questionText,
      promptLabel: PROMPT_LABELS[qId] ?? qId.toUpperCase(),
      colorIndex,
      maxSelections: Math.max(1, first.maxSelections || 1),
      options,
      selected: [],
      randomMode: 'shuffle',
      cycleIndex: 0,
    };
    result.push(choice);
  }

  return result;
}

function groupByAnswer(
  rowsForQ: typeof rows,
): Array<(typeof rows)[number][]> {
  const out: (typeof rows)[number][][] = [];
  let current: (typeof rows)[number][] | null = null;
  for (const row of rowsForQ) {
    if (row.answerLabel) {
      current = [row];
      out.push(current);
    } else if (current) {
      current.push(row);
    }
  }
  return out;
}

const PRIORITY_OVERRIDES: Record<string, string[]> = {
  'area below rooster': ['Q2', 'Q1'],
};

export function seedTargets(): Target[] {
  const order: string[] = [];
  const priorityMap = new Map<string, string[]>();

  for (const row of rows) {
    if (!row.updateTargets) continue;
    const { text } = parseOr(row.updateTargets);
    if (!text) continue;
    const key = text.toLowerCase();
    if (!priorityMap.has(key)) {
      priorityMap.set(key, []);
      order.push(text);
    }
    const priority = priorityMap.get(key)!;
    if (row.qId && !priority.includes(row.qId)) priority.push(row.qId);
  }

  let seq = 0;
  return order.map((name) => {
    const key = name.toLowerCase();
    const derived = priorityMap.get(key) ?? [];
    const override = PRIORITY_OVERRIDES[key];
    const priority = override
      ? [...override.filter((q) => derived.includes(q)), ...derived.filter((q) => !override.includes(q))]
      : derived;
    return {
      id: `target-${(seq++).toString(36)}`,
      name,
      priority,
    };
  });
}

interface AreaSeedSpec {
  x: number;
  y: number;
  includePhoto: boolean;
  targets: Array<{ name: string; description: string }>;
}

const DEFAULT_AREA_SEEDS: AreaSeedSpec[] = [
  {
    x: 0,
    y: 0,
    includePhoto: false,
    targets: [
      { name: 'back-facing skeleton (bottom left)', description: 'the bottom calaca character' },
      { name: 'falling skeleton (top left)', description: 'the top calaca character' },
      { name: 'floating cracked skull', description: 'cracked skull in the lower right of image A' },
      { name: 'floating tophat', description: 'tophat in the lower left' },
      { name: 'two hands above rooster tail', description: 'two bony hands behind the feathers in the upper right' },
      { name: 'agave plant', description: 'the agave plant in the bottom center' },
    ],
  },
  {
    x: 778,
    y: 0,
    includePhoto: false,
    targets: [
      { name: 'sword-wielding skeleton (bottom)', description: 'the sword wielding calaca character (bottom)' },
      { name: 'soldier skeleton (top)', description: 'the tumbling airborne soldier (top) character' },
      { name: 'floating soldier hat skull', description: 'the floating, detached, open-mouthed skull head wearing a soldier hat (lower left)' },
    ],
  },
  {
    x: 483,
    y: 0,
    includePhoto: true,
    targets: [
      { name: 'foating ribs', description: 'the floating isolated ribs (lower right)' },
      { name: 'companion', description: '' },
      { name: 'rooster and rider', description: 'the rider on the rooster' },
      { name: 'area below rooster', description: 'the area below the rooster (lower left of image A just below the skeleton rider\'s foot)' },
    ],
  },
];

let areaSeq = 0;
let areaTargetSeq = 0;

export function seedAreas(targets: Target[]): Area[] {
  const byName = new Map<string, Target>();
  for (const t of targets) byName.set(t.name.toLowerCase(), t);

  const out: Area[] = [];
  for (const spec of DEFAULT_AREA_SEEDS) {
    const areaTargets: AreaTarget[] = [];
    for (const t of spec.targets) {
      const match = byName.get(t.name.toLowerCase());
      if (!match) continue;
      areaTargets.push({
        id: `at-${(areaTargetSeq++).toString(36)}`,
        targetId: match.id,
        description: t.description,
      });
    }
    out.push({
      id: `area-${(areaSeq++).toString(36)}`,
      x: spec.x,
      y: spec.y,
      aspectRatio: '1:1',
      imageSize: '0.5K',
      includeBase: true,
      includePhoto: spec.includePhoto,
      hidden: false,
      targets: areaTargets,
      extraImages: [],
    });
  }
  return out;
}
